import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import MapView from './components/map';
import { SearchAutoSuggest } from 'components';
import { OptionItem } from 'types';
import {
  fetchMemberStart,
  selectedMembers,
  clearSelectedMember
} from 'redux/members';
import { initialAOIPageStart, unloadAOIPage } from 'redux/areaOfInterest';
import { useComponentMount } from 'utilities/hooks.util';

export const AreaOfInterestsViewer: React.FC = () => {
  const dispatch = useDispatch();
  const members = useSelector(selectedMembers);
  const [orgItems, setOrgItems] = useState<OptionItem[]>([]);
  const [organisationId, setOrganisationId] = useState<number | undefined>();
  const [organisationName, setOrganisationName] = useState<
    string | undefined
  >();
  const [utilityId, setUtilityId] = useState<string | undefined>();
  const [refresh, setRefresh] = useState<boolean>(false);

  const handleSearchChange = (item: OptionItem) => {
    setOrganisationId(Number(item.value));
    const inx = item.label.lastIndexOf(' - ');
    const name = item.label.slice(0, inx);
    const uid = item.label.slice(inx + 3);
    setOrganisationName(name);
    setUtilityId(uid);
  };

  const handleRemoteFilter = (value: string) => {
    const isUtilityId = !isNaN(Number(value)) && value.length >= 5;
    const filter = isUtilityId ? `utilityId:${value}` : `name:%${value}%`;
    const localParams: any = {
      fields: 'id,name,utilityId',
      order: 'name',
      limit: 15,
      offset: 0,
      filter: `role:ASSETOWNER,${filter}`
    };
    dispatch(fetchMemberStart(localParams));
  };

  const resetSearch = () => {
    setOrganisationId(undefined);
    setOrganisationName(undefined);
    setUtilityId(undefined);
    setOrgItems([]);
    setRefresh(true);
  };

  useEffect(() => {
    if (Array.isArray(members)) {
      const items: OptionItem[] = members.map((m: any) => {
        return {
          value: m.id,
          label: `${m.name} - ${m.utilityId}`
        };
      });

      setOrgItems(items);
    }
  }, [members]);

  useEffect(() => {
    organisationId && dispatch(initialAOIPageStart(String(organisationId)));
    return () => {
      dispatch(unloadAOIPage());
    };
  }, [organisationId, dispatch]);

  useEffect(() => {
    refresh && setRefresh(false);
  }, [refresh]);

  useComponentMount(() => {
    return () => {
      dispatch(clearSelectedMember());
    };
  });

  return (
    <div
      id="areaOfInterestsViewer"
      data-testid="areaOfInterestsViewer"
      className="area-of-interests-viewer"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Area of Interest viewer"
        pageDescription="Search and inspect member AOIs"
        enableHeaderGlobalBar={true}
      >
        <div className="aoi-viewer-container">
          {!refresh && (
            <SearchAutoSuggest
              id="authority"
              placeHolderText="Member name or Utility ID"
              size="xl"
              items={orgItems}
              onSelectItem={handleSearchChange}
              remoteFilter={handleRemoteFilter}
            />
          )}
          <MapView
            organisationId={organisationId}
            organisationName={organisationName}
            utilityId={utilityId}
            resetSearch={resetSearch}
          />
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default React.memo(AreaOfInterestsViewer);
